import React from 'react';
import './LandingPage.css';

const PrivacyPolicy = () => {
  return (
    <div className="landing-container">
      <video className="background-video" src='/landingpage-logo-video.mp4' autoPlay loop muted playsInline></video>

      <section className="ai-section">
        <div className="privacy-container">

          <h2>AGEstimation Project</h2>

          {/* ================================================================
              PART 1 — DISCLAIMER & ACCEPTABLE USE POLICY
          ================================================================ */}
          <h3>Disclaimer &amp; Acceptable Use Policy</h3>
          <p><strong>Last Updated:</strong> 11/05/2026</p>

          <h4>1. Purpose of the Platform</h4>
          <p>
            AGEstimation Project is designed <strong>exclusively for educational and training
            purposes</strong> — specifically for supervised academic contexts such as training
            schools, university courses, professional development workshops, and scientific
            research activities carried out under the responsibility of qualified instructors
            or researchers.
          </p>
          <p>
            <strong>This platform is not intended for, and must not be used to support,
            clinical decisions, forensic investigations, legal proceedings, or any
            real-case assessment involving actual subjects.</strong>
          </p>

          <h4>2. Geographic Region Selection and Scientific Purpose</h4>
          <p>
            Several tools within this platform allow users to select a geographic region
            (e.g., Europe, Asia, Africa, America). This selection is used exclusively to
            illustrate how age estimation results vary across different population groups,
            based on published scientific literature. It serves a purely didactic function —
            to demonstrate the influence of population-specific parameters on probabilistic
            models — and must not be used to draw conclusions about actual individuals.
          </p>

          <h4>3. Acceptable Use</h4>
          <p>By accessing and using this platform, you explicitly agree that:</p>
          <ul>
            <li>
              You will upload <strong>only anonymised radiological sample images</strong> —
              i.e., images that contain no patient-identifying information (no name, date of
              birth, patient ID, or any other direct or indirect personal data).
            </li>
            <li>
              You are solely responsible for ensuring that any image you upload has been
              properly anonymised in accordance with applicable data protection regulations
              (EU Regulation 2016/679 — GDPR, and applicable national health data legislation)
              <strong> before</strong> uploading it to this platform.
            </li>
            <li>
              You will use this platform only within <strong>supervised training
              contexts</strong> under the guidance of a qualified professional or educator.
            </li>
            <li>
              You will not use any result or report generated by this platform as the sole
              or primary basis for any clinical, diagnostic, forensic, legal, or
              administrative decision.
            </li>
            <li>
              You will not attempt to re-identify any individual from images or data present
              on the platform.
            </li>
            <li>
              When using the AI-assisted annotation feature (Tool 2 — AI Version), you
              acknowledge that the sub-images you annotate are sent to an external AI
              service (Roboflow) for automated analysis, as described in the Privacy Policy
              below.
            </li>
          </ul>

          <h4>4. Limitation of Liability</h4>
          <p>The operators and developers of AGEstimation Project:</p>
          <ul>
            <li>
              <strong>Are not responsible</strong> for the content of images or data uploaded
              by users, nor for verifying that uploaded images have been properly anonymised —
              this responsibility lies entirely and exclusively with the uploading user.
            </li>
            <li>
              <strong>Do not guarantee</strong> the accuracy, completeness, or fitness for
              any specific purpose of the age estimation results produced by the platform.
            </li>
            <li>
              <strong>Accept no liability</strong> for any damages, legal consequences,
              professional sanctions, or losses of any kind arising from misuse of the
              platform, from uploading non-anonymised data, or from applying results outside
              the intended educational and training context.
            </li>
            <li>
              Provide the age estimation algorithms <strong>strictly as educational
              tools</strong> based on published scientific literature. The outputs of these
              algorithms do not constitute medical opinions, forensic expert opinions, or
              legal evidence of any kind.
            </li>
          </ul>
          <p>
            Any use of this platform outside the supervised training context described above
            is undertaken entirely at the user's own risk and responsibility.
          </p>

          <hr />

          {/* ================================================================
              PART 2 — PRIVACY POLICY (GDPR)
          ================================================================ */}
          <h3>Privacy Policy</h3>
          <p><strong>Last Updated:</strong> 11/05/2026</p>
          <p>
            This Privacy Policy describes how AGEstimation Project collects and processes
            personal data of registered users and website visitors in compliance with EU
            Regulation 2016/679 (General Data Protection Regulation — GDPR).
          </p>

          <h4>1. Data Controller</h4>
          <p>
            The data controller is the operator of the AGEstimation Project platform.<br />
            <strong>Contact:</strong> alberto.lino.masiero@gmail.com
          </p>

          <h4>2. Data We Collect</h4>

          <p><strong>a. Account registration data</strong></p>
          <ul>
            <li>Email address, first name, last name, professional role, institution/university name (where applicable)</li>
            <li>Date and timestamp of acceptance of these Terms</li>
            <li>Authentication method (email/password or Google Sign-In)</li>
          </ul>

          <p><strong>b. Session and annotation data</strong></p>
          <ul>
            <li>
              Measurement values, polygon annotations, estimated results, timestamps, and
              tool-specific parameters (tooth type, position, biological sex of the sample,
              geographic region selected for the probabilistic model) associated with your
              account sessions.
            </li>
            <li>
              Per the Acceptable Use Policy above, this data must not contain any
              patient-identifying information. The geographic region field is used solely to
              demonstrate population-specific model variations and does not constitute
              collection of racial or ethnic origin data of any real individual.
            </li>
            <li>
              Session data is stored both <strong>locally in your browser</strong> (via
              IndexedDB, including radiological images in encrypted base64 format) and
              <strong>on Firebase cloud infrastructure</strong> (Firestore for metadata,
              Firebase Storage for images). Local storage persists until you clear your
              browser data.
            </li>
          </ul>

          <p><strong>c. Contact form data</strong></p>
          <ul>
            <li>
              If you submit a message via the contact form on the landing page, we collect
              your name, email address, and message content. This data is transmitted via
              EmailJS (see Section 5) and used solely to respond to your enquiry.
            </li>
          </ul>

          <p><strong>d. Technical data</strong></p>
          <ul>
            <li>
              IP address, browser type, and device information collected automatically by
              the hosting infrastructure (standard server logs). A trial credit balance
              (initially 30 credits) is also associated with each account to manage platform
              access; this may be used in future freemium service tiers.
            </li>
          </ul>

          <h4>3. Legal Basis for Processing (GDPR Art. 6)</h4>
          <ul>
            <li>
              <strong>Account and contact data:</strong> performance of the service contract
              and/or consent (Art. 6(1)(a) and (b)).
            </li>
            <li>
              <strong>Session and annotation data:</strong> legitimate interest in providing
              and improving the platform (Art. 6(1)(f)).
            </li>
            <li>
              <strong>Geographic region parameter:</strong> processed exclusively for the
              purpose of demonstrating population-specific scientific models in a training
              context (Art. 6(1)(f) — legitimate educational interest). This parameter
              refers to the geographic origin of the anonymised radiological sample, not
              of the user, and does not constitute profiling of the user's own ethnic or
              racial origin.
            </li>
          </ul>

          <h4>4. How We Use Your Data</h4>
          <ul>
            <li>To authenticate users and manage accounts securely.</li>
            <li>To synchronise your training sessions across devices.</li>
            <li>To operate and maintain the platform and its educational tools.</li>
            <li>To respond to contact form enquiries.</li>
            <li>To manage trial credit balances and, in future, any freemium service tiers.</li>
          </ul>
          <p>
            We do <strong>not</strong> sell your personal data or share it with third parties
            for commercial, marketing, or any purposes unrelated to platform operation.
          </p>

          <h4>5. Third-Party Data Processors</h4>

          <p><strong>a. Google Firebase (Google LLC, USA)</strong></p>
          <p>
            Cloud infrastructure for user authentication (Firebase Auth, including Google
            Sign-In), database (Firestore), and file storage (Firebase Storage). Google
            processes data under a Data Processing Agreement compliant with GDPR, including
            Standard Contractual Clauses (SCCs) for transfers outside the EU.
          </p>

          <p><strong>b. Roboflow Inc. (USA) — AI-assisted annotation only</strong></p>
          <p>
            The AI-assisted annotation feature (Tool 2 — AI Version) sends the radiological
            sub-image being annotated to Roboflow's inference API to automatically detect
            tooth and pulp regions. <strong>Only the image content is transmitted; no
            account data or user identifiers are included in this request.</strong> Users
            should ensure that images sent for AI annotation are properly anonymised before
            upload, in accordance with the Acceptable Use Policy. Roboflow's privacy
            practices are governed by their own privacy policy.
          </p>

          <p><strong>c. EmailJS (EmailJS Ltd.)</strong></p>
          <p>
            The landing page contact form uses EmailJS to transmit messages (name, email,
            message content) to the platform operators. EmailJS acts as a data processor for
            this purpose only.
          </p>

          <h4>6. Platform Administration</h4>
          <p>
            A designated platform administrator account has access to all user sessions for
            the purposes of platform maintenance, technical support, quality assurance of
            training data, and compliance monitoring. This access is restricted to authorised
            personnel and is governed by confidentiality obligations.
          </p>

          <h4>7. Data Retention</h4>
          <ul>
            <li>Account data is retained for the duration of the active account.</li>
            <li>Session data is retained until deleted by the user or upon account deletion.</li>
            <li>
              Following a deletion request, personal data will be removed from Firebase
              Firestore and Firebase Storage <strong>within 360 days</strong>. Local browser
              data (IndexedDB) is not within our control and must be cleared by the user
              directly via their browser settings.
            </li>
            <li>
              Contact form submissions are retained only as long as necessary to respond to
              the enquiry and are not stored in a long-term database by this platform.
            </li>
          </ul>

          <h4>8. Your Rights (GDPR Art. 15–22)</h4>
          <p>You have the right to:</p>
          <ul>
            <li><strong>Access</strong> your personal data held by this platform.</li>
            <li><strong>Rectify</strong> inaccurate or incomplete account data.</li>
            <li><strong>Erase</strong> your personal data ("right to be forgotten").</li>
            <li><strong>Restrict</strong> processing in certain circumstances.</li>
            <li><strong>Data portability</strong> where technically feasible.</li>
            <li><strong>Object</strong> to processing based on legitimate interests.</li>
          </ul>
          <p>
            To exercise any of these rights, contact us at{' '}
            <strong>alberto.lino.masiero@gmail.com</strong>. We will respond within 30 days.
          </p>
          <p>
            You also have the right to lodge a complaint with the Italian Data Protection
            Authority: <strong>Garante per la Protezione dei Dati Personali</strong> —{' '}
            <a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer">
              www.garanteprivacy.it
            </a>.
          </p>

          <h4>9. Security</h4>
          <p>
            We implement appropriate technical and organisational measures to protect your
            data, including encryption in transit and at rest via Firebase infrastructure.
            In the event of a personal data breach that is likely to result in a high risk
            to your rights and freedoms, we will notify you as required by GDPR Art. 34.
          </p>

          <h4>10. Changes to This Policy</h4>
          <p>
            We may update this policy to reflect changes in our practices or applicable legal
            requirements. Significant changes will be communicated via email or platform
            notification prior to taking effect. Continued use of the platform after
            notification constitutes acceptance of the updated policy.
          </p>

          <hr />

          <p>
            <strong>
              By registering and using AGEstimation Project, you confirm that you have read,
              understood, and agreed to both the Disclaimer &amp; Acceptable Use Policy and
              this Privacy Policy.
            </strong>
          </p>

        </div>
      </section>

      <footer className="landing-footer">
        <p>&copy; {new Date().getFullYear()} Age Estimation Platform. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
